import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, FilterIcon, ChevronLeftIcon, ChevronRightIcon, DownloadIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, XCircleIcon, CalendarIcon, PrinterIcon, MailIcon, MoreHorizontalIcon } from 'lucide-react';
import { SendEmailModal } from '../../components/admin/settlements/SendEmailModal';
import { PrintOptionsModal } from '../../components/admin/settlements/PrintOptionsModal';
import { StatusChangeModal } from '../../components/admin/settlements/StatusChangeModal';
export const SettlementsManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState('last30');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Modal states
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  // Sample data for settlements
  const settlements = [{
    id: 'S-2023-05-001',
    dealerName: 'TechHub Singapore',
    dealerId: 'D-001',
    amount: 12489.5,
    currency: 'SGD',
    ordersCount: 47,
    status: 'completed',
    paymentDate: '2023-05-15',
    createdDate: '2023-05-10',
    paymentMethod: 'Bank Transfer',
    transactionId: 'TRX-9876543210',
    dealerEmail: 'finance@techhub.sg'
  }, {
    id: 'S-2023-05-002',
    dealerName: 'Mobile Relation',
    dealerId: 'D-002',
    amount: 7865.25,
    currency: 'SGD',
    ordersCount: 32,
    status: 'pending',
    paymentDate: null,
    createdDate: '2023-05-12',
    paymentMethod: 'Bank Transfer',
    transactionId: null,
    dealerEmail: 'accounts@mobilerelation.com'
  }, {
    id: 'S-2023-05-003',
    dealerName: 'Wmart',
    dealerId: 'D-003',
    amount: 15720.8,
    currency: 'SGD',
    ordersCount: 65,
    status: 'processing',
    paymentDate: null,
    createdDate: '2023-05-14',
    paymentMethod: 'Bank Transfer',
    transactionId: null,
    dealerEmail: 'finance@wmart.com'
  }, {
    id: 'S-2023-04-001',
    dealerName: 'GameXtreme',
    dealerId: 'D-004',
    amount: 5430.2,
    currency: 'SGD',
    ordersCount: 28,
    status: 'failed',
    paymentDate: null,
    createdDate: '2023-04-25',
    paymentMethod: 'Bank Transfer',
    transactionId: 'TRX-9876543209',
    dealerEmail: 'payments@gamextreme.com'
  }, {
    id: 'S-2023-04-002',
    dealerName: 'TechHub Singapore',
    dealerId: 'D-001',
    amount: 9876.4,
    currency: 'SGD',
    ordersCount: 41,
    status: 'completed',
    paymentDate: '2023-04-20',
    createdDate: '2023-04-15',
    paymentMethod: 'Bank Transfer',
    transactionId: 'TRX-9876543208',
    dealerEmail: 'finance@techhub.sg'
  }];
  // Format date function
  const formatDate = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  // Filter settlements based on search query and filter status
  const filteredSettlements = settlements.filter(settlement => {
    const matchesSearch = settlement.id.toLowerCase().includes(searchQuery.toLowerCase()) || settlement.dealerName.toLowerCase().includes(searchQuery.toLowerCase()) || settlement.dealerId.toLowerCase().includes(searchQuery.toLowerCase());
    if (filterStatus === 'all') return matchesSearch;
    return matchesSearch && settlement.status === filterStatus;
  });
  // Pagination logic
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredSettlements.length / itemsPerPage);
  const paginatedSettlements = filteredSettlements.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const handlePageChange = page => {
    setCurrentPage(page);
  };
  const handleSearch = e => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };
  // Action handlers
  const handleSendEmail = settlement => {
    setSelectedSettlement(settlement);
    setEmailModalOpen(true);
  };
  const handlePrint = settlement => {
    setSelectedSettlement(settlement);
    setPrintModalOpen(true);
  };
  const handleStatusChange = settlement => {
    setSelectedSettlement(settlement);
    setStatusModalOpen(true);
    setActionMenuOpen(null);
  };
  const toggleActionMenu = settlementId => {
    if (actionMenuOpen === settlementId) {
      setActionMenuOpen(null);
    } else {
      setActionMenuOpen(settlementId);
    }
  };
  // Status badge renderer
  const renderStatusBadge = status => {
    switch (status) {
      case 'completed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-2060">
            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-2061" />
            Completed
          </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-2062">
            <ClockIcon className="h-3 w-3 mr-1" data-id="element-2063" />
            Pending
          </span>;
      case 'processing':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2064">
            <ClockIcon className="h-3 w-3 mr-1" data-id="element-2065" />
            Processing
          </span>;
      case 'failed':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-2066">
            <XCircleIcon className="h-3 w-3 mr-1" data-id="element-2067" />
            Failed
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-2068">
            <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-2069" />
            Unknown
          </span>;
    }
  };
  // Calculate summary statistics
  const totalSettlements = settlements.length;
  const completedSettlements = settlements.filter(s => s.status === 'completed').length;
  const pendingSettlements = settlements.filter(s => s.status === 'pending' || s.status === 'processing').length;
  const totalAmount = settlements.reduce((sum, s) => sum + s.amount, 0);
  // Simulate loading
  const refreshData = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 800);
  };
  return <div className="space-y-6" data-id="element-2070">
      {/* Header Section */}
      <div className="flex justify-between items-center" data-id="element-2071">
        <div data-id="element-2072">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2073">
            Settlement Management
          </h1>
          <p className="text-sm text-gray-500" data-id="element-2074">
            Manage payment settlements between SellUp and dealers
          </p>
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors" onClick={() => refreshData()} data-id="element-2075">
          Create New Settlement
        </button>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2076">
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200" data-id="element-2077">
          <div className="flex justify-between items-start" data-id="element-2078">
            <div data-id="element-2079">
              <p className="text-sm font-medium text-gray-500" data-id="element-2080">
                Total Settlements
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2081">
                {totalSettlements}
              </p>
            </div>
            <div className="p-2 bg-blue-100 rounded-md" data-id="element-2082">
              <DownloadIcon className="h-5 w-5 text-blue-600" data-id="element-2083" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200" data-id="element-2084">
          <div className="flex justify-between items-start" data-id="element-2085">
            <div data-id="element-2086">
              <p className="text-sm font-medium text-gray-500" data-id="element-2087">Completed</p>
              <p className="text-2xl font-bold text-green-600 mt-1" data-id="element-2088">
                {completedSettlements}
              </p>
            </div>
            <div className="p-2 bg-green-100 rounded-md" data-id="element-2089">
              <CheckCircleIcon className="h-5 w-5 text-green-600" data-id="element-2090" />
            </div>
          </div>
          <div className="mt-2 text-xs text-green-600 flex items-center" data-id="element-2091">
            <span data-id="element-2092">12% from last month</span>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200" data-id="element-2093">
          <div className="flex justify-between items-start" data-id="element-2094">
            <div data-id="element-2095">
              <p className="text-sm font-medium text-gray-500" data-id="element-2096">Pending</p>
              <p className="text-2xl font-bold text-yellow-600 mt-1" data-id="element-2097">
                {pendingSettlements}
              </p>
            </div>
            <div className="p-2 bg-yellow-100 rounded-md" data-id="element-2098">
              <ClockIcon className="h-5 w-5 text-yellow-600" data-id="element-2099" />
            </div>
          </div>
          <div className="mt-2 text-xs text-red-600 flex items-center" data-id="element-2100">
            <span data-id="element-2101">3% from last month</span>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-5 border border-gray-200" data-id="element-2102">
          <div className="flex justify-between items-start" data-id="element-2103">
            <div data-id="element-2104">
              <p className="text-sm font-medium text-gray-500" data-id="element-2105">Total Amount</p>
              <p className="text-2xl font-bold text-gray-900 mt-1" data-id="element-2106">
                $
                {totalAmount.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </p>
            </div>
            <div className="p-2 bg-purple-100 rounded-md" data-id="element-2107">
              <DownloadIcon className="h-5 w-5 text-purple-600" data-id="element-2108" />
            </div>
          </div>
          <div className="mt-2 text-xs text-green-600 flex items-center" data-id="element-2109">
            <span data-id="element-2110">8.5% from last month</span>
          </div>
        </div>
      </div>
      {/* Search and Filter Section */}
      <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200" data-id="element-2111">
        <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-2112">
          <div className="relative flex-grow" data-id="element-2113">
            <input type="text" placeholder="Search by settlement ID or dealer..." value={searchQuery} onChange={handleSearch} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-2114" />
            <SearchIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" data-id="element-2115" />
          </div>
          <div className="flex items-center space-x-2" data-id="element-2116">
            <div className="relative" data-id="element-2117">
              <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50" data-id="element-2118">
                <FilterIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-2119" />
                <span data-id="element-2120">Filter</span>
              </button>
              {isFilterOpen && <div className="absolute right-0 mt-2 w-72 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-2121">
                  <div className="p-4" data-id="element-2122">
                    <h3 className="font-medium text-gray-900 mb-3" data-id="element-2123">
                      Filter Settlements
                    </h3>
                    <div className="mb-4" data-id="element-2124">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2125">
                        Status
                      </label>
                      <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" data-id="element-2126">
                        <option value="all" data-id="element-2127">All Statuses</option>
                        <option value="completed" data-id="element-2128">Completed</option>
                        <option value="pending" data-id="element-2129">Pending</option>
                        <option value="processing" data-id="element-2130">Processing</option>
                        <option value="failed" data-id="element-2131">Failed</option>
                      </select>
                    </div>
                    <div className="mb-4" data-id="element-2132">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2133">
                        Date Range
                      </label>
                      <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="w-full border border-gray-300 rounded-md p-2 text-sm" data-id="element-2134">
                        <option value="last7" data-id="element-2135">Last 7 days</option>
                        <option value="last30" data-id="element-2136">Last 30 days</option>
                        <option value="last90" data-id="element-2137">Last 90 days</option>
                        <option value="custom" data-id="element-2138">Custom Range</option>
                      </select>
                    </div>
                    {dateRange === 'custom' && <div className="grid grid-cols-2 gap-2 mb-4" data-id="element-2139">
                        <div data-id="element-2140">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-2141">
                            Start Date
                          </label>
                          <div className="relative" data-id="element-2142">
                            <input type="date" className="w-full border border-gray-300 rounded-md p-2 text-sm pr-8" data-id="element-2143" />
                            <CalendarIcon className="absolute right-2 top-2 h-4 w-4 text-gray-400" data-id="element-2144" />
                          </div>
                        </div>
                        <div data-id="element-2145">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-2146">
                            End Date
                          </label>
                          <div className="relative" data-id="element-2147">
                            <input type="date" className="w-full border border-gray-300 rounded-md p-2 text-sm pr-8" data-id="element-2148" />
                            <CalendarIcon className="absolute right-2 top-2 h-4 w-4 text-gray-400" data-id="element-2149" />
                          </div>
                        </div>
                      </div>}
                    <div className="flex justify-between" data-id="element-2150">
                      <button onClick={() => {
                    setFilterStatus('all');
                    setDateRange('last30');
                  }} className="text-sm text-gray-500 hover:text-gray-700" data-id="element-2151">
                        Reset Filters
                      </button>
                      <button onClick={() => setIsFilterOpen(false)} className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700" data-id="element-2152">
                        Apply
                      </button>
                    </div>
                  </div>
                </div>}
            </div>
            <button className="flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50" data-id="element-2153">
              <DownloadIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-2154" />
              <span data-id="element-2155">Export</span>
            </button>
          </div>
        </div>
      </div>
      {/* Settlements Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-2156">
        <div className="overflow-x-auto" data-id="element-2157">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2158">
            <thead className="bg-gray-50" data-id="element-2159">
              <tr data-id="element-2160">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2161">
                  Settlement ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2162">
                  Dealer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2163">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2164">
                  Orders
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2165">
                  Created Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2166">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2167">
                  Payment Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2168">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2169">
              {isLoading ?
            // Skeleton loading rows
            [...Array(5)].map((_, index) => <tr key={`skeleton-${index}`} className="animate-pulse" data-id="element-2170">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2171">
                        <div className="h-4 bg-gray-200 rounded w-24" data-id="element-2172"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2173">
                        <div className="h-4 bg-gray-200 rounded w-32 mb-2" data-id="element-2174"></div>
                        <div className="h-3 bg-gray-200 rounded w-16" data-id="element-2175"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2176">
                        <div className="h-4 bg-gray-200 rounded w-20" data-id="element-2177"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2178">
                        <div className="h-4 bg-gray-200 rounded w-10" data-id="element-2179"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2180">
                        <div className="h-4 bg-gray-200 rounded w-24" data-id="element-2181"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2182">
                        <div className="h-6 bg-gray-200 rounded w-20" data-id="element-2183"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2184">
                        <div className="h-4 bg-gray-200 rounded w-24" data-id="element-2185"></div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-2186">
                        <div className="h-8 bg-gray-200 rounded w-20 ml-auto" data-id="element-2187"></div>
                      </td>
                    </tr>) : paginatedSettlements.map(settlement => <tr key={settlement.id} className="hover:bg-gray-50 group" data-id="element-2188">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2189">
                        <Link to={`/admin/settlements/${settlement.id}`} className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline" data-id="element-2190">
                          {settlement.id}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2191">
                        <div className="text-sm text-gray-900" data-id="element-2192">
                          {settlement.dealerName}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-2193">
                          ID: {settlement.dealerId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2194">
                        <div className="text-sm font-medium text-gray-900" data-id="element-2195">
                          {settlement.currency}{' '}
                          {settlement.amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2196">
                        <div className="text-sm text-gray-900" data-id="element-2197">
                          {settlement.ordersCount}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2198">
                        <div className="text-sm text-gray-900" data-id="element-2199">
                          {formatDate(settlement.createdDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2200">
                        {renderStatusBadge(settlement.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2201">
                        <div className="text-sm text-gray-900" data-id="element-2202">
                          {settlement.paymentDate ? formatDate(settlement.paymentDate) : '-'}
                        </div>
                        {settlement.transactionId && <div className="text-xs text-gray-500" data-id="element-2203">
                            TRX: {settlement.transactionId}
                          </div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-2204">
                        <div className="flex items-center justify-end space-x-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2205">
                          <button onClick={() => handleSendEmail(settlement)} className="text-gray-500 hover:text-blue-600 p-1" aria-label="Send Email" data-id="element-2206">
                            <MailIcon className="h-4 w-4" data-id="element-2207" />
                          </button>
                          <button onClick={() => handlePrint(settlement)} className="text-gray-500 hover:text-blue-600 p-1" aria-label="Print" data-id="element-2208">
                            <PrinterIcon className="h-4 w-4" data-id="element-2209" />
                          </button>
                          <div className="relative" data-id="element-2210">
                            <button onClick={() => toggleActionMenu(settlement.id)} className="text-gray-500 hover:text-blue-600 p-1" aria-label="More Actions" data-id="element-2211">
                              <MoreHorizontalIcon className="h-4 w-4" data-id="element-2212" />
                            </button>
                            {actionMenuOpen === settlement.id && <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-2213">
                                <div className="py-1" data-id="element-2214">
                                  {settlement.status !== 'completed' && <button onClick={() => handleStatusChange(settlement)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-2215">
                                      Change Status
                                    </button>}
                                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-2216">
                                    Download Invoice
                                  </button>
                                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-2217">
                                    Download Receipt
                                  </button>
                                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-2218">
                                    Download Orders Report
                                  </button>
                                </div>
                              </div>}
                          </div>
                        </div>
                      </td>
                    </tr>)}
              {!isLoading && paginatedSettlements.length === 0 && <tr data-id="element-2219">
                  <td colSpan={8} className="px-6 py-10 text-center text-gray-500" data-id="element-2220">
                    No settlements found matching your criteria
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {filteredSettlements.length > 0 && !isLoading && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-2221">
            <div className="text-sm text-gray-500" data-id="element-2222">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredSettlements.length)}{' '}
              of {filteredSettlements.length} settlements
            </div>
            <div className="flex items-center space-x-2" data-id="element-2223">
              <button onClick={() => handlePageChange(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-2224">
                <ChevronLeftIcon className="h-5 w-5" data-id="element-2225" />
              </button>
              {Array.from({
            length: totalPages
          }, (_, i) => i + 1).map(page => <button key={page} onClick={() => handlePageChange(page)} className={`px-3 py-1 rounded-md ${currentPage === page ? 'bg-blue-100 text-blue-600 font-medium' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-2226">
                  {page}
                </button>)}
              <button onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages || totalPages === 0} className={`p-2 rounded-md ${currentPage === totalPages || totalPages === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-2227">
                <ChevronRightIcon className="h-5 w-5" data-id="element-2228" />
              </button>
            </div>
          </div>}
      </div>
      {/* Modals */}
      {emailModalOpen && selectedSettlement && <SendEmailModal settlement={selectedSettlement} onClose={() => setEmailModalOpen(false)} onSend={() => {
      // Handle send logic
      setEmailModalOpen(false);
    }} data-id="element-2229" />}
      {printModalOpen && selectedSettlement && <PrintOptionsModal settlement={selectedSettlement} onClose={() => setPrintModalOpen(false)} data-id="element-2230" />}
      {statusModalOpen && selectedSettlement && <StatusChangeModal settlement={selectedSettlement} onClose={() => setStatusModalOpen(false)} onStatusChange={newStatus => {
      // Handle status change logic
      setStatusModalOpen(false);
    }} data-id="element-2231" />}
    </div>;
};